/**
 * Central currency utilities for Breakin' Bread.
 *
 * - `countryToCurrency` / `localeToCurrency`: resolve a sensible display currency
 *   from the visitor's geo-IP country (preferred) or locale (fallback).
 * - `convert()`: convert an amount between currencies using USD-based rates
 *   (as returned by /api/fx).
 * - `formatCurrency()`: locale-aware formatting via Intl.NumberFormat.
 *
 * Rates are fetched (and cached daily) by /api/fx and consumed client-side via
 * the `useCurrency` hook. All amounts in Firestore keep their *original* stored
 * currency; conversion is a presentation concern only.
 */

export type Rates = Record<string, number>; // ISO 4217 -> units per 1 USD

/** ISO 3166-1 alpha-2 country -> ISO 4217 currency. Mirrors middleware geo detection. */
export const countryToCurrency: Record<string, string> = {
  // North America
  US: 'USD', CA: 'CAD', MX: 'MXN',
  // Eurozone
  DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR', NL: 'EUR', BE: 'EUR', AT: 'EUR',
  IE: 'EUR', PT: 'EUR', FI: 'EUR', GR: 'EUR', SK: 'EUR', SI: 'EUR', LT: 'EUR',
  LV: 'EUR', EE: 'EUR', LU: 'EUR', CY: 'EUR', MT: 'EUR', HR: 'EUR',
  // Rest of Europe
  GB: 'GBP', CH: 'CHF', SE: 'SEK', NO: 'NOK', DK: 'DKK', PL: 'PLN', CZ: 'CZK',
  HU: 'HUF', RO: 'RON', BG: 'BGN', UA: 'UAH', RU: 'RUB', TR: 'TRY', RS: 'RSD',
  IS: 'ISK',
  // Africa
  ZA: 'ZAR', NG: 'NGN', GH: 'GHS', KE: 'KES', TZ: 'TZS', UG: 'UGX', ZM: 'ZMW',
  ZW: 'USD', RW: 'RWF', BW: 'BWP', NA: 'NAD', MW: 'MWK', SO: 'SOS', MG: 'MGA',
  CD: 'CDF', CG: 'XAF', CF: 'XAF', CM: 'XAF', ER: 'ERN', LS: 'LSL', GM: 'GMD',
  SN: 'XOF', CI: 'XOF', BF: 'XOF', ML: 'XOF', NE: 'XOF', TG: 'XOF', BJ: 'XOF',
  EG: 'EGP', MA: 'MAD', DZ: 'DZD', TN: 'TND', ET: 'ETB', AO: 'AOA', MZ: 'MZN',
  // Middle East
  SA: 'SAR', AE: 'AED', IL: 'ILS', QA: 'QAR', KW: 'KWD', BH: 'BHD', OM: 'OMR',
  JO: 'JOD', LB: 'LBP', IQ: 'IQD', IR: 'IRR',
  // Asia
  IN: 'INR', CN: 'CNY', JP: 'JPY', KR: 'KRW', ID: 'IDR', MY: 'MYR', SG: 'SGD',
  TH: 'THB', VN: 'VND', PH: 'PHP', PK: 'PKR', BD: 'BDT', TW: 'TWD', HK: 'HKD',
  MM: 'MMK', KH: 'KHR', AF: 'AFN', LK: 'LKR', NP: 'NPR',
  // Oceania
  AU: 'AUD', NZ: 'NZD',
  // Latin America
  BR: 'BRL', AR: 'ARS', CL: 'CLP', CO: 'COP', PE: 'PEN', VE: 'VES', UY: 'UYU',
  BO: 'BOB', PY: 'PYG', CR: 'CRC', GT: 'GTQ', DO: 'DOP', PA: 'USD', EC: 'USD',
};

/** Locale -> currency, used only when geo country is unavailable. */
export const localeToCurrency: Record<string, string> = {
  en: 'USD', es: 'EUR', fr: 'EUR', de: 'EUR', pt: 'EUR', it: 'EUR', nl: 'EUR',
  sv: 'SEK', da: 'DKK', no: 'NOK', fi: 'EUR', pl: 'PLN', ru: 'RUB', uk: 'UAH',
  cs: 'CZK', ro: 'RON', hu: 'HUF', sk: 'EUR', hr: 'EUR', sr: 'RSD', bg: 'BGN',
  sl: 'EUR', lt: 'EUR', lv: 'EUR', et: 'EUR', el: 'EUR', tr: 'TRY', ar: 'SAR',
  he: 'ILS', fa: 'IRR', hi: 'INR', bn: 'BDT', ur: 'PKR', ta: 'INR', te: 'INR',
  mr: 'INR', gu: 'INR', pa: 'INR', ml: 'INR', th: 'THB', vi: 'VND', id: 'IDR',
  ms: 'MYR', tl: 'PHP', ko: 'KRW', ja: 'JPY', zh: 'CNY', 'zh-TW': 'TWD',
  my: 'MMK', km: 'KHR', sw: 'KES', am: 'ETB', ca: 'EUR',
  // African languages
  ha: 'NGN', yo: 'NGN', ig: 'NGN', zu: 'ZAR', xh: 'ZAR', af: 'ZAR', so: 'SOS',
  om: 'ETB', wo: 'XOF', sn: 'USD', ny: 'MWK', ln: 'CDF', rw: 'RWF', lg: 'UGX',
  ti: 'ETB', tn: 'BWP', st: 'LSL', mg: 'MGA', sg: 'XAF', ak: 'GHS',
};

export const DEFAULT_CURRENCY = 'USD';

/** Resolve a display currency from an optional country and/or locale. */
export function resolveCurrency(country?: string | null, locale?: string | null): string {
  if (country) {
    const c = countryToCurrency[country.toUpperCase()];
    if (c) return c;
  }
  if (locale) {
    const byExact = localeToCurrency[locale];
    if (byExact) return byExact;
    const base = locale.split('-')[0];
    if (localeToCurrency[base]) return localeToCurrency[base];
  }
  return DEFAULT_CURRENCY;
}

/**
 * Convert `amount` from one currency to another using USD-based rates
 * (rates[X] = how many X per 1 USD). Returns the original amount if either
 * currency is missing from the rate table.
 */
export function convert(
  amount: number,
  from: string,
  to: string,
  rates: Rates | null | undefined
): number {
  if (!rates) return amount;
  const f = from.toUpperCase();
  const t = to.toUpperCase();
  if (f === t) return amount;
  const rf = f === 'USD' ? 1 : rates[f];
  const rt = t === 'USD' ? 1 : rates[t];
  if (!rf || !rt) return amount; // unknown currency -> no conversion
  const usd = amount / rf;
  return usd * rt;
}

/** Locale-aware currency formatting. Falls back gracefully on bad input. */
export function formatCurrency(amount: number, currency: string, locale = 'en'): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency.toUpperCase(),
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    // Unknown currency code etc. — fall back to a plain number + code.
    return `${amount.toFixed(2)} ${currency.toUpperCase()}`;
  }
}

/** Minimal offline fallback rates (approx) so the UI still renders if /api/fx fails. */
export const FALLBACK_RATES: Rates = {
  USD: 1, EUR: 0.92, GBP: 0.79, SEK: 10.5, NOK: 10.8, DKK: 6.9, CHF: 0.88,
  ZAR: 18.5, NGN: 1500, KES: 129, GHS: 15, INR: 83, BRL: 5.1, CAD: 1.36,
  AUD: 1.52, JPY: 150, CNY: 7.2, MXN: 17, PLN: 4.0, CZK: 23, HUF: 360,
};
